import { getIdToken } from 'firebase/auth';

import { auth } from 'external/firebase/index';

import { BASE_CLOUD_FUNCTION_URL } from './functionsBaseUrl';
import { formatErrorMessage } from './utils';

// Field names mirror the scraper service's JSON response verbatim.
/* eslint-disable camelcase */
// One real chat turn's outcome, once its SSE stream's `done` event lands —
// see streamChatMessage below. No `reply` field: the caller already has the
// full text, built up token by token via onToken as it streamed in.
export interface ChatStreamResult {
    session_id: string;
    conversation_id: number;
    is_new_conversation: boolean;
}

export interface DailyChatStats {
    date: string;
    user_messages: number;
    distinct_users: number;
}

// The course module a reply was grounded in (e.g. "JavaScript", "Python (2
// lygis)", "HTML ir CSS") — see cao-content-service's messages.module
// migration for why this is the raw per-lecture value, not a course-level
// grouping. Same date window as `daily`, most-asked-about first.
export interface TopicStats {
    topic: string;
    message_count: number;
}

export interface ChatStats {
    total_conversations: number;
    total_user_messages: number;
    distinct_users: number;
    messages_today: number;
    distinct_users_today: number;
    refusal_count: number;
    refusal_rate: number;
    daily: DailyChatStats[];
    top_topics: TopicStats[];
    rate_limited_users_today: number;
}
/* eslint-enable camelcase */

// Splits an SSE byte stream on the blank-line `\n\n` event separator,
// tolerating chunk boundaries that land mid-event (fetch's reader hands back
// arbitrary byte chunks, not one per event) by keeping whatever's left over
// in `buffer` between calls.
const extractSseEvents = (buffer: string): { events: Record<string, unknown>[]; rest: string } => {
    const events: Record<string, unknown>[] = [];
    let rest = buffer;
    let separatorIndex = rest.indexOf('\n\n');
    while (separatorIndex !== -1) {
        const rawEvent = rest.slice(0, separatorIndex).trim();
        rest = rest.slice(separatorIndex + 2);
        if (rawEvent.startsWith('data: ')) {
            try {
                events.push(JSON.parse(rawEvent.slice('data: '.length)));
            } catch {
                // Malformed frame — drop it rather than throw mid-stream.
            }
        }
        separatorIndex = rest.indexOf('\n\n');
    }
    return { events, rest };
};

// Sends one chat turn to the `chat` Cloud Function as Server-Sent Events, so the
// reply can be animated onto the screen token by token (like kursuok.lt) instead
// of appearing all at once. `sessionId` is omitted on the very first turn; the
// final `done` event carries the server-assigned `session_id` to reuse on
// subsequent turns. `onToken` fires once per text delta, in order; the promise
// resolves only once the stream's `done` event has landed (or rejects/returns
// `ok: false` on any failure, including one signaled in-band mid-stream — see
// the Cloud Function proxy's own comment on why that has to be in-band here).
export const streamChatMessage = async (
    message: string,
    sessionId: string | undefined,
    onToken: (delta: string) => void,
): Promise<ChatStreamResult | { ok: false; code: string }> => {
    try {
        if (!auth.currentUser) {
            throw new Error('auth/no-user-found');
        }
        const token = await getIdToken(auth.currentUser);
        const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ session_id: sessionId, message }),
        });

        if (!response.ok) {
            // Only a pre-stream failure (auth, validation, rate limit, pilot
            // gating) reaches here as plain JSON — see the Cloud Function
            // proxy: once its SSE response has started, every failure is an
            // in-band event further down, never a non-2xx status.
            const data = await response.json().catch(() => null);
            if (data?.error) {
                throw new Error(data.error);
            }
            if (response.status === 403) {
                throw new Error('auth/forbidden-access');
            }
            throw new Error(`chat/failed-${response.status}`);
        }
        if (!response.body) {
            throw new Error('chat/upstream-error');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let result: ChatStreamResult | null = null;
        let inBandError: string | null = null;

        const handleEvent = (event: Record<string, unknown>): void => {
            if (typeof event.token === 'string') {
                onToken(event.token);
            } else if (typeof event.error === 'string') {
                inBandError = event.error;
            } else if (event.done === true) {
                result = {
                    session_id: event.session_id as string,
                    conversation_id: event.conversation_id as number,
                    is_new_conversation: event.is_new_conversation as boolean,
                };
            }
        };

        for (;;) {
            // eslint-disable-next-line no-await-in-loop
            const { value, done } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const { events, rest } = extractSseEvents(buffer);
            buffer = rest;
            events.forEach(handleEvent);
        }

        if (inBandError) {
            throw new Error(inBandError);
        }
        if (!result) {
            throw new Error('chat/upstream-error');
        }
        return result;
    } catch (error: any) {
        if (error?.message?.includes('CORS') || error?.message?.includes('Failed to fetch')) {
            return { ok: false, code: 'something_went_wrong' };
        }
        return formatErrorMessage(error);
    }
};

// Fetches the chat observability numbers for the super-admin insights page.
// The `chat` Cloud Function's `/stats` route is gated by authorizeSuperAdmin
// server-side — this call fails with a 403 for anyone else, same as any other
// super-admin-only endpoint in this app.
export const fetchChatStats = async (days: number): Promise<ChatStats | { ok: false; code: string }> => {
    try {
        if (!auth.currentUser) {
            throw new Error('auth/no-user-found');
        }
        const token = await getIdToken(auth.currentUser);
        const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/chat/stats?days=${days}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('auth/forbidden-access');
            }
            throw new Error(data?.error || `chat/failed-${response.status}`);
        }
        return data as ChatStats;
    } catch (error: any) {
        if (error?.message?.includes('CORS') || error?.message?.includes('Failed to fetch')) {
            return { ok: false, code: 'something_went_wrong' };
        }
        return formatErrorMessage(error);
    }
};
